import React, { useState, useEffect } from 'react';
import {
  db, collection, addDoc, doc, updateDoc,
  onSnapshot, query, orderBy, deleteDoc, serverTimestamp
} from '../firebase';
import { categories } from '../data';
import { useAuth } from '../AuthContext';
import toast from 'react-hot-toast';
import './AdminPanel.css';

const TABS = [
  { id:'dashboard', label:'Dashboard',  icon:'📊' },
  { id:'orders',    label:'Orders',     icon:'📦' },
  { id:'products',  label:'Products',   icon:'🛒' },
  { id:'payments',  label:'Payments',   icon:'💳' },
  { id:'customers', label:'Customers',  icon:'👥' },
  { id:'analytics', label:'Analytics',  icon:'📈' },
];

const BLANK_PRODUCT = {
  name:'', category:'fruits', price:'', unit:'kg',
  stock:'', badge:'', description:'',
  image:'', rating:4.5, reviews:0,
};

export default function AdminPanel({ navigate }) {
  const { user, logout } = useAuth();
  const [tab,          setTab]          = useState('dashboard');
  const [orders,       setOrders]       = useState([]);
  const [payments,     setPayments]     = useState([]);
  const [customers,    setCustomers]    = useState([]);
  const [fbProducts,   setFbProducts]   = useState([]);
  const [sideOpen,     setSideOpen]     = useState(true);
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [newProduct,   setNewProduct]   = useState(BLANK_PRODUCT);
  const [addLoading,   setAddLoading]   = useState(false);
  const [searchOrder,  setSearchOrder]  = useState('');

  // ── Real-time listeners ────────────────────────────────────
  useEffect(() => {
    // Orders
    const unsubOrders = onSnapshot(
      query(collection(db,'orders'), orderBy('createdAt','desc')),
      snap => setOrders(snap.docs.map(d => ({ fireId:d.id, ...d.data() })))
    );
    // Payments
    const unsubPay = onSnapshot(
      query(collection(db,'payments'), orderBy('createdAt','desc')),
      snap => setPayments(snap.docs.map(d => ({ fireId:d.id, ...d.data() })))
    );
    // Products
    const unsubProd = onSnapshot(
      collection(db,'products'),
      snap => setFbProducts(snap.docs.map(d => ({ fireId:d.id, ...d.data() })))
    );
    // Customers (users collection)
    const unsubUsers = onSnapshot(
      collection(db,'users'),
      snap => setCustomers(snap.docs.map(d => ({ fireId:d.id, ...d.data() })))
    );
    return () => { unsubOrders(); unsubPay(); unsubProd(); unsubUsers(); };
  }, []);

  // ── Stats ──────────────────────────────────────────────────
  const totalRevenue  = orders.reduce((s,o) => s + (o.total||0), 0);
  const pendingOrders = orders.filter(o => o.status !== 'delivered').length;
  const todayOrders   = orders.filter(o => {
    if (!o.createdAt?.toDate) return false;
    return o.createdAt.toDate().toDateString() === new Date().toDateString();
  }).length;
  const paidAmount = payments.filter(p=>p.status==='paid').reduce((s,p)=>s+(p.amount||0),0);

  // ── Update order status ────────────────────────────────────
  const updateOrderStatus = async (fireId, newStatus) => {
    await updateDoc(doc(db,'orders',fireId), { status: newStatus });
    toast.success('Order status updated!');
  };

  // ── Add product to Firestore ───────────────────────────────
  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price || !newProduct.stock) {
      toast.error('Fill name, price and stock'); return;
    }
    setAddLoading(true);
    try {
      await addDoc(collection(db,'products'), {
        ...newProduct,
        price:  Number(newProduct.price),
        stock:  Number(newProduct.stock),
        rating: Number(newProduct.rating),
        reviews: 0,
        color:  '#1DB954',
        createdAt: serverTimestamp(),
      });
      toast.success('✅ Product added to Firebase!');
      setNewProduct(BLANK_PRODUCT);
      setShowAddForm(false);
    } catch(e) {
      toast.error('Failed: ' + e.message);
    }
    setAddLoading(false);
  };

  // ── Delete product ─────────────────────────────────────────
  const deleteProduct = async (fireId, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await deleteDoc(doc(db,'products', fireId));
    toast.success('Product deleted');
  };

  const filteredOrders = orders.filter(o =>
    !searchOrder ||
    o.id?.toLowerCase().includes(searchOrder.toLowerCase()) ||
    o.customer?.name?.toLowerCase().includes(searchOrder.toLowerCase()) ||
    o.customer?.phone?.includes(searchOrder)
  );

  const fmtTime = ts => {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'});
  };

  return (
    <div className="admin-page">
      {/* ── Sidebar ── */}
      <aside className={`admin-sidebar ${sideOpen?'open':'collapsed'}`}>
        <div className="admin-sidebar-header">
          <div className="admin-brand">🌿 {sideOpen && <span>FreshMart</span>}</div>
          <button className="sidebar-toggle" onClick={()=>setSideOpen(!sideOpen)}>{sideOpen?'◀':'▶'}</button>
        </div>
        <div className="admin-user-card">
          <div className="admin-avatar">👑</div>
          {sideOpen && <div className="admin-user-info"><p className="admin-user-name">{user?.name}</p><p className="admin-user-role">Administrator</p></div>}
        </div>
        <nav className="admin-nav">
          {TABS.map(t => (
            <button key={t.id} className={`admin-nav-item ${tab===t.id?'active':''}`} onClick={()=>setTab(t.id)}>
              <span className="nav-icon">{t.icon}</span>{sideOpen && <span>{t.label}</span>}
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button className="admin-nav-item" onClick={()=>navigate('home')}>
            <span className="nav-icon">🏠</span>{sideOpen && <span>Go to Store</span>}
          </button>
          <button className="admin-nav-item logout-btn" onClick={()=>{logout();navigate('login');}}>
            <span className="nav-icon">🚪</span>{sideOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="admin-main">
        <div className="admin-topbar">
          <div className="topbar-left">
            <h1 className="admin-page-title">{TABS.find(t=>t.id===tab)?.icon} {TABS.find(t=>t.id===tab)?.label}</h1>
            <p className="admin-page-sub">🔴 Live · {new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}</p>
          </div>
          <div className="topbar-right">
            <div className="live-dot">● LIVE</div>
            <div className="admin-avatar-small">👑</div>
          </div>
        </div>

        <div className="admin-content">

          {/* ════════ DASHBOARD ════════ */}
          {tab==='dashboard' && (
            <div>
              <div className="stats-grid">
                {[
                  { label:'Total Revenue',    value:`₹${totalRevenue.toLocaleString()}`, icon:'💰', color:'#1DB954', sub:'+12% this week' },
                  { label:'Total Orders',     value:orders.length,      icon:'📦', color:'#2196F3', sub:`${todayOrders} today` },
                  { label:'Registered Users', value:customers.length,   icon:'👥', color:'#9C27B0', sub:'Firebase Auth' },
                  { label:'Products (DB)',     value:fbProducts.length,  icon:'🛒', color:'#FF9800', sub:'In Firestore' },
                  { label:'Pending Orders',   value:pendingOrders,      icon:'⏳', color:'#EF5350', sub:'Need action' },
                  { label:'Paid Online',       value:`₹${paidAmount.toLocaleString()}`, icon:'💳', color:'#00BCD4', sub:'UPI + Card' },
                ].map(s=>(
                  <div className="stat-card" key={s.label} style={{'--stat-color':s.color}}>
                    <div className="stat-icon">{s.icon}</div>
                    <div className="stat-info">
                      <p className="stat-value">{s.value}</p>
                      <p className="stat-label">{s.label}</p>
                      <p className="stat-change">{s.sub}</p>
                    </div>
                    <div className="stat-glow" />
                  </div>
                ))}
              </div>

              <div className="dashboard-section">
                <div className="section-hdr"><h3>📦 Recent Orders (Live)</h3><button onClick={()=>setTab('orders')}>View All →</button></div>
                {orders.length===0
                  ? <div className="empty-state"><span>📭</span><p>No orders yet. Orders will appear here in real-time.</p></div>
                  : <OrdersTable orders={orders.slice(0,6)} onStatusChange={updateOrderStatus} fmtTime={fmtTime} />
                }
              </div>

              <div className="dashboard-section">
                <div className="section-hdr"><h3>💳 Recent Payments (Live)</h3><button onClick={()=>setTab('payments')}>View All →</button></div>
                {payments.length===0
                  ? <div className="empty-state"><span>💸</span><p>No payments recorded yet.</p></div>
                  : <PaymentsTable payments={payments.slice(0,5)} fmtTime={fmtTime} />
                }
              </div>
            </div>
          )}

          {/* ════════ ORDERS ════════ */}
          {tab==='orders' && (
            <div>
              <div className="section-toolbar">
                <div className="search-box-admin">
                  <span>🔍</span>
                  <input placeholder="Search by Order ID, Name, Phone…" value={searchOrder} onChange={e=>setSearchOrder(e.target.value)} />
                </div>
                <div className="order-counts">
                  <span>Total: <strong>{orders.length}</strong></span>
                  <span style={{color:'#EF5350'}}>Pending: <strong>{pendingOrders}</strong></span>
                  <span style={{color:'#4CAF50'}}>Delivered: <strong>{orders.filter(o=>o.status==='delivered').length}</strong></span>
                </div>
              </div>
              {filteredOrders.length===0
                ? <div className="empty-state"><span>📭</span><p>No orders found</p></div>
                : <OrdersTable orders={filteredOrders} onStatusChange={updateOrderStatus} fmtTime={fmtTime} full />
              }
            </div>
          )}

          {/* ════════ PRODUCTS ════════ */}
          {tab==='products' && (
            <div>
              <div className="section-toolbar">
                <h3>{fbProducts.length} Products in Firebase</h3>
                <button className="add-product-btn" onClick={()=>setShowAddForm(!showAddForm)}>
                  {showAddForm ? '✕ Cancel' : '+ Add Product'}
                </button>
              </div>

              {showAddForm && (
                <div className="add-product-form">
                  <h3>➕ Add New Product</h3>
                  <div className="apf-grid">
                    {[
                      { key:'name',        label:'Product Name *', type:'text',   placeholder:'e.g. Organic Apples' },
                      { key:'price',       label:'Price (₹) *',    type:'number', placeholder:'89' },
                      { key:'unit',        label:'Unit',           type:'text',   placeholder:'kg / piece / 500g' },
                      { key:'stock',       label:'Stock Qty *',    type:'number', placeholder:'50' },
                      { key:'badge',       label:'Badge',          type:'text',   placeholder:'Organic / Best Seller' },
                      { key:'rating',      label:'Rating',         type:'number', placeholder:'4.5' },
                      { key:'image',       label:'Image URL',      type:'text',   placeholder:'https://images.unsplash.com/…' },
                    ].map(f=>(
                      <div key={f.key} className="form-group">
                        <label>{f.label}</label>
                        <input type={f.type} placeholder={f.placeholder} value={newProduct[f.key]}
                          onChange={e=>setNewProduct(p=>({...p,[f.key]:e.target.value}))} />
                      </div>
                    ))}
                    <div className="form-group">
                      <label>Category</label>
                      <select value={newProduct.category} onChange={e=>setNewProduct(p=>({...p,category:e.target.value}))}>
                        {categories.filter(c=>c.id!=='all').map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group full">
                      <label>Description</label>
                      <input type="text" placeholder="Short description of the product" value={newProduct.description}
                        onChange={e=>setNewProduct(p=>({...p,description:e.target.value}))} />
                    </div>
                  </div>
                  {newProduct.image && (
                    <div className="img-preview">
                      <img src={newProduct.image} alt="preview" onError={e=>e.target.style.display='none'} />
                      <span>Image Preview</span>
                    </div>
                  )}
                  <button className="save-product-btn" onClick={handleAddProduct} disabled={addLoading}>
                    {addLoading ? '⏳ Saving to Firebase…' : '🔥 Save to Firebase'}
                  </button>
                </div>
              )}

              {fbProducts.length===0
                ? <div className="empty-state"><span>🛒</span><p>No products in Firebase yet. Add your first product above!</p></div>
                : (
                  <div className="admin-products-grid">
                    {fbProducts.map(p=>(
                      <div className="admin-product-card" key={p.fireId}>
                        <div className="ap-img-wrap">
                          {p.image
                            ? <img src={p.image} alt={p.name} onError={e=>e.target.style.display='none'} />
                            : <div className="ap-no-img">📦</div>
                          }
                          {p.badge && <span className="ap-badge">{p.badge}</span>}
                        </div>
                        <div className="ap-info">
                          <p className="ap-category">{p.category}</p>
                          <h4 className="ap-name">{p.name}</h4>
                          <div className="ap-meta">
                            <span className="ap-price">₹{p.price}/{p.unit}</span>
                            <span className="ap-rating">⭐ {p.rating}</span>
                          </div>
                          <span className={`stock-badge ${p.stock<20?'low':'ok'}`}>
                            {p.stock<20?'⚠️ Low':'✅ OK'}: {p.stock} units
                          </span>
                        </div>
                        <div className="ap-actions">
                          <button className="ap-btn edit" onClick={()=>toast('Edit feature coming soon!')}>✏️ Edit</button>
                          <button className="ap-btn delete" onClick={()=>deleteProduct(p.fireId,p.name)}>🗑️ Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              }
            </div>
          )}

          {/* ════════ PAYMENTS ════════ */}
          {tab==='payments' && (
            <div>
              <div className="pay-stats-row">
                {[
                  { label:'Total Collected', value:`₹${totalRevenue.toLocaleString()}`, icon:'💰' },
                  { label:'Online Paid',     value:`₹${paidAmount.toLocaleString()}`,  icon:'📱' },
                  { label:'COD Pending',     value:`₹${(totalRevenue-paidAmount).toLocaleString()}`, icon:'💵' },
                  { label:'Transactions',    value:payments.length, icon:'🧾' },
                ].map(s=>(
                  <div className="pay-stat" key={s.label}>
                    <span>{s.icon}</span>
                    <div><p className="ps-val">{s.value}</p><p className="ps-label">{s.label}</p></div>
                  </div>
                ))}
              </div>
              {payments.length===0
                ? <div className="empty-state"><span>💸</span><p>No payment records yet.</p></div>
                : <PaymentsTable payments={payments} fmtTime={fmtTime} full />
              }
            </div>
          )}

          {/* ════════ CUSTOMERS ════════ */}
          {tab==='customers' && (
            <div>
              <div className="section-toolbar"><h3>{customers.length} Registered Users</h3></div>
              {customers.length===0
                ? <div className="empty-state"><span>👥</span><p>No users yet. Users appear here after signup.</p></div>
                : (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead><tr><th>#</th><th>User</th><th>Email</th><th>Role</th><th>Joined</th><th>Orders</th></tr></thead>
                      <tbody>
                        {customers.map((c,i)=>(
                          <tr key={c.fireId}>
                            <td>{i+1}</td>
                            <td><div className="customer-row"><div className="cust-avatar">{(c.name||'U')[0].toUpperCase()}</div><strong>{c.name||'Unknown'}</strong></div></td>
                            <td>{c.email}</td>
                            <td><span className="role-badge">{c.role||'user'}</span></td>
                            <td className="td-muted">{fmtTime(c.createdAt)}</td>
                            <td><strong>{orders.filter(o=>o.userId===c.uid).length}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              }
            </div>
          )}

          {/* ════════ ANALYTICS ════════ */}
          {tab==='analytics' && (
            <div className="analytics-grid">
              <div className="analytics-card">
                <h3>📦 Orders by Status</h3>
                {['confirmed','preparing','picked_up','on_the_way','delivered'].map(s=>{
                  const count = orders.filter(o=>o.status===s).length;
                  const pct   = orders.length ? Math.round((count/orders.length)*100) : 0;
                  return (
                    <div className="analytics-bar-row" key={s}>
                      <span className="bar-label">{s.replace(/_/g,' ')}</span>
                      <div className="bar-track"><div className="bar-fill" style={{width:`${pct}%`,background:'#1DB954'}} /></div>
                      <span className="bar-val">{count}</span>
                    </div>
                  );
                })}
              </div>
              <div className="analytics-card">
                <h3>💰 Revenue Summary</h3>
                <div className="rev-stats">
                  {[
                    ['Total Revenue', `₹${totalRevenue.toLocaleString()}`],
                    ['Total Orders',  orders.length],
                    ['Avg Order',     `₹${orders.length?Math.round(totalRevenue/orders.length):0}`],
                    ['Today Orders',  todayOrders],
                  ].map(([l,v])=>(
                    <div className="rev-item" key={l}><p className="rev-val">{v}</p><p className="rev-label">{l}</p></div>
                  ))}
                </div>
              </div>
              <div className="analytics-card">
                <h3>💳 Payment Methods</h3>
                {['cod','upi','card'].map(pm=>{
                  const count = payments.filter(p=>p.method===pm).length;
                  const pct   = payments.length ? Math.round((count/payments.length)*100) : 0;
                  const icons = {cod:'💵',upi:'📱',card:'💳'};
                  return (
                    <div className="analytics-bar-row" key={pm}>
                      <span className="bar-label">{icons[pm]} {pm.toUpperCase()}</span>
                      <div className="bar-track"><div className="bar-fill" style={{width:`${pct}%`,background:'#2196F3'}} /></div>
                      <span className="bar-val">{count}</span>
                    </div>
                  );
                })}
              </div>
              <div className="analytics-card">
                <h3>🛒 Products by Category</h3>
                {categories.filter(c=>c.id!=='all').map(cat=>{
                  const count = fbProducts.filter(p=>p.category===cat.id).length;
                  const pct   = fbProducts.length ? Math.round((count/fbProducts.length)*100) : 0;
                  return (
                    <div className="analytics-bar-row" key={cat.id}>
                      <span className="bar-label">{cat.icon} {cat.name}</span>
                      <div className="bar-track"><div className="bar-fill" style={{width:`${pct}%`,background:cat.color}} /></div>
                      <span className="bar-val">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="admin-bottom-nav">
        <div className="admin-bottom-nav-inner">
          {[
            { id:"dashboard", label:"Home",     icon:"📊" },
            { id:"orders",    label:"Orders",   icon:"📦" },
            { id:"products",  label:"Products", icon:"🛒" },
            { id:"payments",  label:"Payments", icon:"💳" },
            { id:"analytics", label:"Stats",    icon:"📈" },
          ].map(t => (
            <button
              key={t.id}
              className={"abn-item " + (tab===t.id?"active":"")}
              onClick={() => setTab(t.id)}
            >
              <span className="abn-icon">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

/* ─── Sub-components ─── */
function OrdersTable({ orders, onStatusChange, fmtTime, full }) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Order ID</th><th>Customer</th>
            {full && <><th>Phone</th><th>Items</th></>}
            <th>Total</th><th>Payment</th><th>Status</th><th>Date</th><th>Update</th>
          </tr>
        </thead>
        <tbody>
          {orders.map(o=>(
            <tr key={o.fireId}>
              <td><code className="order-code">{o.id?.slice(-10)||o.fireId?.slice(0,10)}</code></td>
              <td><strong>{o.customer?.name||'Guest'}</strong>{full&&<><br/><small style={{color:'#6B7A8D'}}>{o.customer?.city}</small></>}</td>
              {full && <>
                <td>{o.customer?.phone||'-'}</td>
                <td style={{maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{o.items?.map(i=>i.name).join(', ')}</td>
              </>}
              <td className="td-amount">₹{o.total?.toLocaleString()}</td>
              <td><span className="pay-badge">{(o.paymentMethod||'cod').toUpperCase()}</span></td>
              <td><StatusBadge status={o.status} /></td>
              <td className="td-muted" style={{fontSize:11}}>{fmtTime(o.createdAt)}</td>
              <td>
                <select className="status-select" value={o.status||'confirmed'} onChange={e=>onStatusChange(o.fireId,e.target.value)}>
                  {['confirmed','preparing','picked_up','on_the_way','delivered'].map(s=>(
                    <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTable({ payments, fmtTime, full }) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr><th>Txn ID</th><th>Customer</th><th>Amount</th><th>Method</th><th>Status</th><th>Date</th></tr>
        </thead>
        <tbody>
          {payments.map(p=>(
            <tr key={p.fireId}>
              <td><code>{p.orderId?.slice(-8)||p.fireId?.slice(0,8)}</code></td>
              <td>{p.customerName||'Guest'}</td>
              <td className="td-amount">₹{p.amount?.toLocaleString()}</td>
              <td><span className="pay-badge">{(p.method||'cod').toUpperCase()}</span></td>
              <td>
                <span style={{
                  padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                  background: p.status==='paid' ? 'rgba(76,175,80,.15)' : 'rgba(255,152,0,.15)',
                  color: p.status==='paid' ? '#4CAF50' : '#FF9800',
                }}>
                  {p.status==='paid' ? '✅ Paid' : '⏳ Pending'}
                </span>
              </td>
              <td className="td-muted" style={{fontSize:11}}>{fmtTime(p.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    confirmed:  { label:'Confirmed',  color:'#2196F3' },
    preparing:  { label:'Preparing',  color:'#FF9800' },
    picked_up:  { label:'Picked Up',  color:'#9C27B0' },
    on_the_way: { label:'On the Way', color:'#00BCD4' },
    delivered:  { label:'Delivered',  color:'#4CAF50' },
  };
  const s = map[status] || { label:status||'-', color:'#999' };
  return (
    <span className="status-badge" style={{background:s.color+'22',color:s.color,border:`1px solid ${s.color}44`}}>
      {s.label}
    </span>
  );
}
